import { getToken } from '../../../../../utils/themeTokens';
import { readableTextColor } from './colorUtils';

/**
 * Screen-space chrome drawn on top of the fabric scene: the image frame and a
 * class-name chip per annotation.
 *
 * It is painted in `after:render` rather than added as fabric objects so the
 * chips stay a constant size at any zoom, never become selectable, and never
 * end up in the annotation arrays or the undo stack.
 */

const CHIP_HEIGHT = 18;
const CHIP_PAD_X = 6;
const CHIP_GAP = 3;
const CHIP_FONT = '600 11px Inter, -apple-system, "Segoe UI", Roboto, sans-serif';
/** Below this on-screen size a box gets a locator ring so it stays findable. */
const TINY_BOX_PX = 10;

const roundRect = (ctx, x, y, w, h, r) => {
  const radius = Math.min(r, h / 2, w / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

const drawImageFrame = (ctx, canvas, image, palette) => {
  const width = Number(image?.originalWidth) || 0;
  const height = Number(image?.originalHeight) || 0;
  if (!width || !height) return;

  const vpt = canvas.viewportTransform || [1, 0, 0, 1, 0, 0];
  const zoom = vpt[0] || 1;
  const left = vpt[4];
  const top = vpt[5];

  ctx.save();
  ctx.strokeStyle = palette.frame;
  ctx.lineWidth = 1;
  ctx.setLineDash([]);
  ctx.strokeRect(
    Math.round(left) + 0.5,
    Math.round(top) + 0.5,
    Math.round(width * zoom),
    Math.round(height * zoom)
  );
  ctx.restore();
};

// measureText runs for every chip on every frame during a drag; the font never
// changes, so the width of a given label is worth remembering.
const textWidthCache = new Map();

const measureLabel = (ctx, text) => {
  const cached = textWidthCache.get(text);
  if (cached !== undefined) return cached;
  const width = ctx.measureText(text).width;
  if (textWidthCache.size > 500) textWidthCache.clear();
  textWidthCache.set(text, width);
  return width;
};

const drawChip = (ctx, canvas, text, x, y, color, { dashed }) => {
  ctx.font = CHIP_FONT;
  const chipWidth = measureLabel(ctx, text) + CHIP_PAD_X * 2;

  // Keep the chip inside the visible canvas so labels never scroll out of reach.
  const left = Math.max(2, Math.min(x, canvas.getWidth() - chipWidth - 2));
  const top = Math.max(2, y);

  ctx.save();
  ctx.globalAlpha = dashed ? 0.85 : 1;
  ctx.fillStyle = color;
  roundRect(ctx, left, top, chipWidth, CHIP_HEIGHT, 4);
  ctx.fill();
  if (dashed) {
    ctx.globalAlpha = 1;
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 2]);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  ctx.globalAlpha = 1;
  ctx.fillStyle = readableTextColor(color);
  ctx.textBaseline = 'middle';
  ctx.fillText(text, left + CHIP_PAD_X, top + CHIP_HEIGHT / 2 + 0.5);
  ctx.restore();
};

const drawLocatorRing = (ctx, rect, color) => {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.globalAlpha = 0.7;
  ctx.setLineDash([2, 2]);
  ctx.beginPath();
  ctx.arc(cx, cy, TINY_BOX_PX, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
};

const drawAnnotationChrome = (ctx, canvas, palette, options) => {
  const viewW = canvas.getWidth();
  const viewH = canvas.getHeight();

  canvas.getObjects().forEach((obj) => {
    const kind = obj?.data?.type;
    if (kind !== 'bbox' && kind !== 'polygon') return;

    const rect = obj.getBoundingRect(false, true);
    if (rect.left > viewW || rect.top > viewH || rect.left + rect.width < 0 || rect.top + rect.height < 0) {
      return;
    }

    const color = obj.classData?.color || obj.stroke || palette.accent;

    if (rect.width < TINY_BOX_PX && rect.height < TINY_BOX_PX) {
      drawLocatorRing(ctx, rect, color);
    }

    if (!options.showLabels) return;

    const name = obj.classData?.name;
    if (!name) return;

    const label = obj.data?.isAI ? `${name} · AI` : name;
    // Prefer sitting above the box; drop inside when the box hugs the top edge.
    const above = rect.top - CHIP_HEIGHT - CHIP_GAP;
    const y = above >= 2 ? above : rect.top + CHIP_GAP;
    drawChip(ctx, canvas, label, rect.left, y, color, { dashed: !!obj.data?.isAI });
  });
};

/**
 * Attach the overlay to a canvas. `getOptions()` is read on every frame and
 * returns `{ image, showLabels }`. Returns a detach function.
 */
export const attachCanvasOverlay = (canvas, getOptions) => {
  if (!canvas) return () => {};

  const palette = {
    frame: getToken('--border-strong', '#cdd4dc'),
    accent: getToken('--primary', '#059669'),
  };

  const handler = (event) => {
    const ctx = event?.ctx || canvas.getContext();
    if (!ctx) return;
    const options = getOptions() || {};
    drawImageFrame(ctx, canvas, options.image, palette);
    drawAnnotationChrome(ctx, canvas, palette, options);
  };

  canvas.on('after:render', handler);
  canvas.requestRenderAll();

  return () => {
    canvas.off('after:render', handler);
  };
};
